import { BellOff, BellRing, CheckCircle2, ChevronLeft, ChevronRight, Filter, LoaderCircle, RefreshCw, Search, X } from "lucide-react";
import { useMemo, useState, type KeyboardEvent } from "react";
import { useSearchParams } from "react-router-dom";
import { useSession } from "../auth/SessionContext";
import { ProblemList } from "../components/ProblemList";
import { Button, Card, ErrorState, Field, LoadingBlock, PageHeader } from "../components/ui";
import { useDebouncedSearch } from "../hooks/useDebouncedSearch";
import { useDocumentTitle } from "../hooks/useDocumentTitle";
import { useRemote } from "../hooks/useRemote";
import { apiRequest, queryString } from "../lib/api";
import { SEVERITY_LABELS } from "../lib/format";
import type { CursorPage, FiltersResponse, ProblemListItem, ProblemSeverity, ProblemStatus } from "../types";

const PROBLEM_VIEWS = [
  { status: "open", label: "Activos", description: "Lo que necesita atención ahora", icon: BellRing },
  { status: "recovered", label: "Historial", description: "Problemas que ocurrieron y se recuperaron", icon: CheckCircle2 },
  { status: "suppressed", label: "Suprimidos", description: "Eventos silenciados por la fuente", icon: BellOff },
] as const;

export function ProblemsPage() {
  const { csrfToken } = useSession();
  const [searchParams, setSearchParams] = useSearchParams();
  const [cursor, setCursor] = useState<string | null>(null);
  const [cursorHistory, setCursorHistory] = useState<Array<string | null>>([]);
  const [acknowledgingId, setAcknowledgingId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const filters = useMemo(() => ({
    q: searchParams.get("q") ?? "",
    severity: searchParams.get("severity") ?? "",
    clientId: searchParams.get("clientId") ?? "",
    region: searchParams.get("region") ?? "",
    status: normalizeProblemStatus(searchParams.get("status")),
    acknowledged: searchParams.get("acknowledged") ?? "",
  }), [searchParams]);

  const requestQuery = queryString({ search: filters.q, severity: filters.severity, clientId: filters.clientId, region: filters.region, status: filters.status, acknowledged: filters.acknowledged, cursor, limit: 100 });
  const remote = useRemote(
    () => apiRequest<CursorPage<ProblemListItem>>(`/api/v1/problems${requestQuery}`),
    [requestQuery],
    30_000,
  );
  const filterOptions = useRemote(() => apiRequest<FiltersResponse>("/api/v1/filters"), [], 300_000);

  const updateFilter = (name: string, value: string) => {
    setCursor(null);
    setCursorHistory([]);
    const next = new URLSearchParams(searchParams);
    value ? next.set(name, value) : next.delete(name);
    setSearchParams(next, { replace: true });
  };

  const updateClientFilter = (value: string) => {
    setCursor(null);
    setCursorHistory([]);
    const next = new URLSearchParams(searchParams);
    value ? next.set("clientId", value) : next.delete("clientId");
    setSearchParams(next, { replace: true });
  };

  const [searchDraft, setSearchDraft, resetSearchDraft] = useDebouncedSearch(filters.q, (value) => updateFilter("q", value));

  const acknowledge = async (problem: ProblemListItem) => {
    if (!problem.alertId) return;
    setAcknowledgingId(problem.id);
    setActionError(null);
    try {
      await apiRequest(`/api/v1/alerts/${encodeURIComponent(problem.alertId)}/acknowledge`, { method: "POST", csrfToken });
      remote.reload();
    } catch (error) {
      setActionError(error instanceof Error ? error.message : "No se pudo marcar la alerta como atendida.");
    } finally {
      setAcknowledgingId(null);
    }
  };

  const nextPage = () => {
    if (remote.loading || remote.error || !remote.data?.nextCursor) return;
    setCursorHistory((history) => [...history, cursor]);
    setCursor(remote.data.nextCursor);
  };

  const previousPage = () => {
    const previous = cursorHistory.at(-1);
    if (remote.loading || previous === undefined) return;
    setCursor(previous);
    setCursorHistory((history) => history.slice(0, -1));
  };

  const moveTabFocus = (event: KeyboardEvent<HTMLElement>) => {
    if (!["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)) return;
    event.preventDefault();
    const current = PROBLEM_VIEWS.findIndex((entry) => entry.status === filters.status);
    const step = event.key === "ArrowRight" ? 1 : PROBLEM_VIEWS.length - 1;
    const target = event.key === "Home" ? 0
      : event.key === "End" ? PROBLEM_VIEWS.length - 1
      : (current + step) % PROBLEM_VIEWS.length;
    const view = PROBLEM_VIEWS[target];
    if (!view) return;
    updateFilter("status", view.status);
    event.currentTarget.querySelectorAll<HTMLButtonElement>("[role=tab]")[target]?.focus();
  };

  const viewCopy = PROBLEM_VIEWS.find((view) => view.status === filters.status) ?? PROBLEM_VIEWS[0];
  useDocumentTitle(`Problemas · ${viewCopy.label}`);
  const hasAdditionalFilters = ["q", "severity", "clientId", "region", "acknowledged"].some((key) => searchParams.has(key));
  const clearFilters = () => {
    resetSearchDraft("");
    setCursor(null);
    setCursorHistory([]);
    setSearchParams(filters.status === "open" ? {} : { status: filters.status });
  };

  return (
    <div className="page">
      <PageHeader
        eyebrow={filters.status === "recovered" ? "Registro operativo" : "Cola operativa"}
        title="Problemas"
        description={filters.status === "recovered" ? "Historial general de incidentes recuperados, ordenado por la recuperación más reciente." : filters.status === "suppressed" ? "Eventos que la fuente marcó como suprimidos." : "Todos los problemas activos, de cualquier severidad, ordenados por atención."}
        actions={<Button variant="secondary" size="small" loading={remote.refreshing} onClick={remote.reload}><RefreshCw size={15} /> Actualizar</Button>}
      />
      <Card className="problem-view-switcher" role="tablist" aria-label="Vista de problemas" onKeyDown={moveTabFocus}>
        {PROBLEM_VIEWS.map((view) => {
          const Icon = view.icon;
          const selected = filters.status === view.status;
          return (
            <button className={`problem-view${selected ? " problem-view--selected" : ""}`} type="button" role="tab" id={`problem-view-${view.status}`} aria-selected={selected} aria-controls="problem-results" tabIndex={selected ? 0 : -1} key={view.status} onClick={() => updateFilter("status", view.status)}>
              <Icon size={18} aria-hidden="true" />
              <span><strong>{view.label}</strong><small>{view.description}</small></span>
            </button>
          );
        })}
      </Card>
      <Card className="filter-bar" aria-label="Filtros de problemas">
        <Field label="Buscar">
          <span className="input-with-icon"><Search size={16} /><input value={searchDraft} onChange={(event) => setSearchDraft(event.target.value)} placeholder="Problema o edificio" /></span>
        </Field>
        <Field label="Severidad">
          <select value={filters.severity} onChange={(event) => updateFilter("severity", event.target.value)}>
            <option value="">Todas</option>
            {(filterOptions.data?.severities ?? ["disaster", "high", "average", "warning", "information", "not_classified"]).map((severity) => (
              <option value={severity} key={severity}>{SEVERITY_LABELS[severity as ProblemSeverity]}</option>
            ))}
          </select>
        </Field>
        <Field label="Cliente">
          <select value={filters.clientId} onChange={(event) => updateClientFilter(event.target.value)}>
            <option value="">Todos</option>
            {(filterOptions.data?.clients ?? []).map((client) => <option value={client.id} key={client.id}>{client.name}</option>)}
          </select>
        </Field>
        <Field label="Región">
          <select value={filters.region} onChange={(event) => updateFilter("region", event.target.value)}>
            <option value="">Todas</option>
            {(filterOptions.data?.regions ?? []).map((region) => <option value={region.name} key={region.id}>{region.name}</option>)}
          </select>
        </Field>
        <Field label="Atención">
          <select value={filters.acknowledged} onChange={(event) => updateFilter("acknowledged", event.target.value)}>
            <option value="">Todos</option>
            <option value="false">Sin atender</option>
            <option value="true">Atendidos</option>
          </select>
        </Field>
        {hasAdditionalFilters ? (
          <Button variant="ghost" size="small" onClick={clearFilters}><X size={15} /> Limpiar</Button>
        ) : <span className="filter-summary"><Filter size={15} /> Vista: {viewCopy.label}</span>}
      </Card>

      {actionError ? <div className="inline-error" role="alert">{actionError}</div> : null}
      <Card className="content-card" id="problem-results" role="tabpanel" aria-labelledby={`problem-view-${filters.status}`} aria-busy={remote.loading}>
        {remote.loading && !remote.data ? <LoadingBlock rows={8} /> : null}
        {remote.error && !remote.data ? <ErrorState error={remote.error} onRetry={remote.reload} /> : null}
        {remote.error && remote.data ? <div className="inline-error" role="alert">No se pudo actualizar la lista. Se muestran los últimos datos disponibles.</div> : null}
        {remote.loading && remote.data ? <p className="list-updating" role="status"><LoaderCircle size={14} className="spin" aria-hidden="true" /> Actualizando resultados…</p> : null}
        {remote.data ? <ProblemList problems={remote.data.items} {...(filters.status === "open" ? { onAcknowledge: acknowledge } : {})} acknowledgingId={acknowledgingId} emptyTitle={filters.status === "recovered" ? "Todavía no hay incidentes recuperados" : filters.status === "suppressed" ? "No hay eventos suprimidos" : "No hay problemas activos"} emptyDescription={filters.status === "recovered" ? "Cuando un DVR o una conexión se recupere, el incidente quedará registrado acá." : filters.status === "suppressed" ? "Los eventos que la fuente silencie aparecerán acá." : "El monitoreo no detecta incidentes que necesiten atención en este momento."} /> : null}
        {remote.data && (remote.data.nextCursor || cursorHistory.length > 0) ? (
          <nav className="pagination" aria-label="Paginación">
            <Button variant="secondary" size="small" disabled={remote.loading || cursorHistory.length === 0} onClick={previousPage}><ChevronLeft size={15} /> Anterior</Button>
            <span>Página {cursorHistory.length + 1}</span>
            <Button variant="secondary" size="small" disabled={remote.loading || Boolean(remote.error) || !remote.data.nextCursor} onClick={nextPage}>Siguiente <ChevronRight size={15} /></Button>
          </nav>
        ) : null}
      </Card>
    </div>
  );
}

function normalizeProblemStatus(value: string | null): ProblemStatus {
  return value === "recovered" || value === "suppressed" ? value : "open";
}
